<script lang="ts">
    import ButtonSmallBlueRounded from '../../shared/ButtonSmallBlueRounded.svelte';
    import { ExportApi } from './ExportApi';
    import { _ } from 'svelte-i18n';

    import { api } from '../../../stores/api';
    import type { ExportType } from './ExportType';
    import { captureMessage } from '@sentry/svelte';
    import { debug } from '../../../stores/store';
    import { Umami } from '../../utilities/Umami';

    export let et: ExportType;
    export let label: string;
    export let downloadingInProgress: boolean;

    let localDownloadingInProgress: boolean = false;
    let enableDownloadCSV: boolean = true;
    let enableDownloadXLSX: boolean = true;

    async function download(et: ExportType): Promise<boolean> {
        downloadingInProgress = true;
        localDownloadingInProgress = true;
        // https://www.programonaut.com/how-to-create-a-download-for-a-zip-file-with-sveltekit-step-by-step/
        let exportFile = await ExportApi.getFile($api, et.getApiEndpoint(), {
            type: et.getType(),
        }).catch(error => {
            console.error('Error downloading file', error);
            alert($_('error_downloading_file'));
            captureMessage('Error downloading file', {
                extra: {
                    error,
                },
            });
        });

        if (!exportFile) {
            downloadingInProgress = false;
            localDownloadingInProgress = false;
            return false;
        }

        // convert exportFile (raw csv file) to blob
        let blob = new Blob([exportFile], { type: et.getType() });

        let url = window.URL || window.webkitURL;
        let link = url.createObjectURL(blob);

        // generate anchor tag, click it for download and then remove it again
        let a = document.createElement('a');
        a.setAttribute('download', et.getName());
        a.setAttribute('href', link);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // revoke the object URL to free up memory
        url.revokeObjectURL(link);
        downloadingInProgress = false;
        localDownloadingInProgress = false;
        return true;
    }

    $: enableDownloadXLSX = enableDownloadXLSX || $debug;
    $: enableDownloadCSV = enableDownloadCSV || $debug;
</script>

<div class="download-file-section-container">
    <div>{label}</div>
    {#if localDownloadingInProgress}
        <div style="display: flex; flex-direction: row; justify-content: flex-start">
            {$_('Pobieranie')}
            {et.getHumanFriendlyType()}...
        </div>
    {:else}
        <div style="display: flex; flex-direction: row; justify-content: flex-end">
            <ButtonSmallBlueRounded
                text={$_('csv')}
                disabled={downloadingInProgress || !enableDownloadCSV}
                on:click={async () => {
                    console.log('export', et.getApiEndpoint());
                    et.setType('text/csv', 'csv');
                    const success = await download(et);
                    if (success) {
                        Umami.event('export_' + et.getApiEndpoint());
                        Umami.event('export_csv', { endpoint: et.getApiEndpoint() });
                    } else {
                        Umami.error('export_csv', null, { endpoint: et.getApiEndpoint() });
                    }
                    enableDownloadCSV = false;
                }}
            />
            <ButtonSmallBlueRounded
                text={$_('xlsx')}
                disabled={downloadingInProgress || !enableDownloadXLSX}
                on:click={async () => {
                    console.log('export', et.getApiEndpoint());
                    et.setType('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx');
                    const success = await download(et);
                    if (success) {
                        Umami.event('export_' + et.getApiEndpoint());
                        Umami.event('export_xlsx', { endpoint: et.getApiEndpoint() });
                    } else {
                        Umami.error('export_xlsx', null, { endpoint: et.getApiEndpoint() });
                    }
                    enableDownloadXLSX = false;
                }}
            />
        </div>
    {/if}
</div>

<style>
    .download-file-section-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 5px 10px 10px;
        border-radius: 5px;
        font-size: var(--font-size-tactical-plan);
    }
</style>
